/**
 * 694 [1] + 695 [1] — one global "session expired" verdict, read at the DISPLAY layer.
 *
 * 694 introduced a single verdict that suppressed ALL error toasts until the next
 * sign-in. That was too broad: after the user pressed "Later" (a deliberate
 * "keep working" choice), an unrelated 500 save failure produced NO on-screen
 * feedback at all — the "silent failure" class we spent the 660s removing.
 *
 * 695 [1] splits the state into two independent signals:
 *   • `expired`    — the session is known dead; drives the persistent banner that
 *                    keeps telling the user WHY saves fail (until re-login).
 *   • `dialogOpen`  — the expiry dialog is currently on screen; ONLY then do we
 *                    suppress a generic error toast (it would otherwise contradict
 *                    the dialog). Once the dialog closes, normal failure notices
 *                    render again.
 *
 * So suppression now hides only the DUPLICATE of the same expiry moment — it never
 * hides a failure. It carries no user data: two in-memory booleans + one callback.
 */

let expired = false;
let dialogOpen = false;
let signInAgainHandler: (() => void) | null = null;

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // a broken listener must not stop the others
    }
  });
}

/** Set the global verdict. `true` = the session is known to be expired. */
export function setSessionExpired(value: boolean): void {
  if (expired === value) return;
  expired = value;
  notify();
}

/** Track whether the expiry dialog is currently on screen (drives toast suppression). */
export function setExpiryDialogOpen(value: boolean): void {
  if (dialogOpen === value) return;
  dialogOpen = value;
  notify();
}

/** Read the current verdict (the persistent banner shows while this is true). */
export function isSessionExpired(): boolean {
  return expired;
}

/** Read whether the expiry dialog is open (the shared Toast suppresses errors only then). */
export function isExpiryDialogOpen(): boolean {
  return dialogOpen;
}

/** Subscribe to verdict / dialog changes (banner + Toast re-render on a flip). */
export function subscribeSessionExpiry(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The global "sign in again" action is owned by useAuth (it decides when to tear
 * the session down and purge offline data). The persistent banner — which can be
 * rendered anywhere — calls this instead of duplicating that logic.
 */
export function registerSignInAgainHandler(fn: (() => void) | null): void {
  signInAgainHandler = fn;
}

/** Ask the owner to run the sign-in-again flow (banner button). */
export function requestSignInAgain(): void {
  signInAgainHandler?.();
}